import { useEffect, useRef } from "react";

import { install } from "resize-observer";

/**
 * Observes an element for size changes and calls the callback with the ResizeObserverEntry.
 * Handles late-mounting refs (e.g., conditionally rendered elements).
 * @param {import("react").RefObject} ref - A React ref attached to the element to observe.
 * @param {function(ResizeObserverEntry): void} callback - Called on each resize.
 */
export default function useResizeObserver2(ref, callback) {
  const callbackRef = useRef(callback);
  callbackRef.current = callback;

  const observedRef = useRef(null);
  const observerRef = useRef(null);

  // No dependency array: re-checks ref.current after every render, which is how
  // a ref that starts null and fills in later gets picked up. The node check is
  // what keeps that cheap — rebuilding the observer every render instead put 539
  // of them through one NavBar section switch, and observe() re-delivers on each
  // one, so the callbacks pile into the same long task.
  useEffect(() => {
    if (!window.ResizeObserver) install();

    const node = ref?.current;
    if (node === observedRef.current) return;

    observerRef.current?.disconnect();
    observedRef.current = node;
    observerRef.current = null;
    if (!node) return;

    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        callbackRef.current(entry);
      }
    });

    observer.observe(node);
    observerRef.current = observer;
  });

  useEffect(
    () => () => {
      observerRef.current?.disconnect();
      observerRef.current = null;
      observedRef.current = null;
    },
    []
  );
}
